/* Novara Shopify Theme — core JavaScript
   Handles: search overlay, mobile menu, cart drawer, product variant picker,
   thumbnail gallery, and AJAX add-to-cart.
   Written as a single IIFE so it stays isolated and doesn't leak globals.
*/
(function () {
  'use strict';

  // ---------- Utilities ----------
  const $ = (s, r) => (r || document).querySelector(s);
  const $$ = (s, r) => Array.from((r || document).querySelectorAll(s));
  const on = (el, ev, fn) => el && el.addEventListener(ev, fn);
  const routes = window.routes || {};

  const formatMoney = (cents) => {
    const amount = (cents / 100).toFixed(2);
    return (window.moneyFormat || '${{amount}}').replace(/\{\{\s*amount\s*\}\}/, amount);
  };

  // ---------- Search overlay ----------
  const searchToggle = $('#SearchToggle');
  const searchOverlay = $('#SearchOverlay');
  const searchClose = $('#SearchClose');
  on(searchToggle, 'click', () => {
    searchOverlay.hidden = false;
    setTimeout(() => searchOverlay.querySelector('input')?.focus(), 50);
  });
  on(searchClose, 'click', () => (searchOverlay.hidden = true));
  on(document, 'keydown', (e) => {
    if (e.key === 'Escape' && searchOverlay && !searchOverlay.hidden) searchOverlay.hidden = true;
  });

  // ---------- Mobile menu ----------
  const menuToggle = $('#MenuToggle');
  const mobileNav = $('#MobileNav');
  on(menuToggle, 'click', () => {
    const open = mobileNav.hidden;
    mobileNav.hidden = !open;
    menuToggle.setAttribute('aria-expanded', open ? 'true' : 'false');
  });

  // ---------- Product thumbnail gallery ----------
  const mainMedia = $('#ProductMediaMain');
  if (mainMedia) {
    $$('.product-media-thumbs button').forEach((btn) => {
      on(btn, 'click', () => {
        const src = btn.getAttribute('data-src');
        if (src) mainMedia.src = src;
        $$('.product-media-thumbs button').forEach((b) => b.removeAttribute('data-active'));
        btn.setAttribute('data-active', '');
      });
    });
  }

  // ---------- Product variant picker ----------
  const productForm = document.querySelector('[data-product-form]');
  const variantScript = document.querySelector('[data-product-json]');
  if (productForm && variantScript) {
    let variants = [];
    try { variants = JSON.parse(variantScript.textContent); } catch {}

    const idInput = productForm.querySelector('input[name="id"]');
    const addBtn = productForm.querySelector('[data-add-to-cart]');
    const priceEl = $('#ProductPrice');

    const getSelectedOptions = () => {
      const optionInputs = productForm.querySelectorAll('input[name^="options["]:checked, select[name^="options["]');
      return Array.from(optionInputs).sort((a, b) => {
        return Number(a.dataset.optionPosition || 0) - Number(b.dataset.optionPosition || 0);
      }).map((i) => i.value);
    };

    const matchVariant = (selected) => {
      return variants.find((v) => JSON.stringify(v.options) === JSON.stringify(selected));
    };

    const render = () => {
      const selected = getSelectedOptions();
      const variant = matchVariant(selected) || variants[0];
      if (!variant) return;
      idInput.value = variant.id;
      if (priceEl) {
        let html = `<span class="price"><span class="price-now">${formatMoney(variant.price)}</span></span>`;
        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          html = `<span class="price price-on-sale"><span class="price-now">${formatMoney(variant.price)}</span><s class="price-compare">${formatMoney(variant.compare_at_price)}</s></span>`;
        }
        priceEl.innerHTML = html;
      }
      if (addBtn) {
        addBtn.disabled = !variant.available;
        addBtn.textContent = variant.available
          ? (addBtn.dataset.addLabel || addBtn.textContent)
          : (addBtn.dataset.soldLabel || 'Sold out');
      }
    };

    productForm.querySelectorAll('input[name^="options["]').forEach((input) => {
      on(input, 'change', render);
    });

    // AJAX add-to-cart (avoid full page reload, refresh count)
    on(productForm, 'submit', async (e) => {
      // If user hit "Buy now" (clicked checkout button), fall through to native submit
      const submitter = e.submitter;
      if (submitter && submitter.name === 'checkout') return;
      e.preventDefault();
      if (!routes.cart_add_url) return;
      try {
        const formData = new FormData(productForm);
        const r = await fetch(routes.cart_add_url, {
          method: 'POST',
          body: formData,
          headers: { Accept: 'application/javascript' },
        });
        if (r.ok) {
          await refreshCartCount();
          // Subtle visual feedback
          addBtn.textContent = '✓ Added';
          setTimeout(() => render(), 1500);
        }
      } catch (err) { console.warn('Add to cart failed:', err); }
    });

    render();
  }

  // ---------- Cart count refresh ----------
  async function refreshCartCount() {
    try {
      const r = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      const data = await r.json();
      const countEl = $('#CartCount');
      if (countEl) {
        countEl.textContent = data.item_count || '';
        countEl.style.display = data.item_count > 0 ? '' : 'none';
      }
    } catch {}
  }
  refreshCartCount();

  // ---------- Cart quantity buttons (cart page) ----------
  $$('[data-qty-increment]').forEach((btn) => {
    on(btn, 'click', () => {
      const input = btn.parentElement.querySelector('input');
      input.value = Number(input.value) + 1;
      input.form?.requestSubmit?.();
    });
  });
  $$('[data-qty-decrement]').forEach((btn) => {
    on(btn, 'click', () => {
      const input = btn.parentElement.querySelector('input');
      const v = Number(input.value) - 1;
      input.value = Math.max(0, v);
      input.form?.requestSubmit?.();
    });
  });

  // ---------- Product recommendations ----------
  // Fetches the related-products section for this product; removes the section when Shopify returns no products.
  if (window.customElements && !customElements.get('product-recommendations')) {
    customElements.define('product-recommendations', class extends HTMLElement {
      connectedCallback() {
        if (this.dataset.loaded) return;
        this.dataset.loaded = 'true';
        const url = this.dataset.url;
        const section = this.closest('.shopify-section') || this;
        const clear = () => {
          if (window.Shopify && window.Shopify.designMode) this.innerHTML = '';
          else section.remove();
        };
        if (!url) return clear();
        fetch(url)
          .then((r) => (r.ok ? r.text() : ''))
          .then((text) => {
            const fresh = new DOMParser().parseFromString(text, 'text/html').querySelector('product-recommendations');
            if (fresh && fresh.querySelector('.product-card')) this.innerHTML = fresh.innerHTML;
            else clear();
          })
          .catch(clear);
      }
    });
  }
})();
